import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useOperationEnvironment } from './OperationEnvironmentContext'

const STORAGE_KEY = 'sa_selected_provider'
const RECENTS_KEY = 'sa_recent_providers'
const MAX_RECENTS = 8

export interface SelectedProvider {
  id: string
  name: string
  reference?: string
  status?: string
  environment?: string
}

interface ProviderSelectorState {
  provider: SelectedProvider | null
  recents: SelectedProvider[]
  setProvider: (p: SelectedProvider) => void
  clearProvider: () => void
}

const ProviderSelectorContext = createContext<ProviderSelectorState>({
  provider: null,
  recents: [],
  setProvider: () => {},
  clearProvider: () => {},
})

function loadFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

export const ProviderSelectorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { environment } = useOperationEnvironment()
  const prevEnvRef = useRef(environment)

  const [provider, setProviderState] = useState<SelectedProvider | null>(() =>
    loadFromStorage<SelectedProvider | null>(STORAGE_KEY, null),
  )
  const [recents, setRecents] = useState<SelectedProvider[]>(() =>
    loadFromStorage<SelectedProvider[]>(RECENTS_KEY, []),
  )

  useEffect(() => {
    if (prevEnvRef.current !== environment) {
      setProviderState(null)
      localStorage.removeItem(STORAGE_KEY)
      prevEnvRef.current = environment
    }
  }, [environment])

  useEffect(() => {
    if (provider) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(provider))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [provider])

  useEffect(() => {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(recents))
  }, [recents])

  const setProvider = useCallback((p: SelectedProvider) => {
    setProviderState(p)
    setRecents(prev => {
      const filtered = prev.filter(r => r.id !== p.id)
      return [p, ...filtered].slice(0, MAX_RECENTS)
    })
  }, [])

  const clearProvider = useCallback(() => {
    setProviderState(null)
  }, [])

  return (
    <ProviderSelectorContext.Provider value={{ provider, recents, setProvider, clearProvider }}>
      {children}
    </ProviderSelectorContext.Provider>
  )
}

export function useProviderSelector() {
  return useContext(ProviderSelectorContext)
}
